/**
 * DISTRIBUTION-MAP MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// Map export: rasterizes the live <svg> map and composites an optional title
// and legend onto a canvas, then exports PNG / PDF / PPTX.
//
// PNG  — native canvas.toBlob.
// PDF  — jsPDF (lazy-imported), page sized to the image.
// PPTX — pptxgenjs (lazy-imported), single slide with the image centered.
//
// Everything is rendered from the CURRENT on-screen SVG, so exported colors,
// fills, and assignments always match what the user sees.

import { VIEWBOX, pickInk } from "../../../../../map-core/render.js";
import { CALLOUT } from "../../../../../map-core/config.js";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const SCALE = 2; // 2x for crisp raster output
// Fraction of the map height where the Northeast callout column (VT box) starts.
// The right-side legend aligns its top here, matching the on-screen editor.
const VB_H = Number(VIEWBOX.split(/\s+/)[3]) || 600;
const CALLOUT_TOP_FRAC = CALLOUT.top / VB_H;

// --- rasterize a standalone <svg> element to a canvas ---------------------
function svgToImage(svgEl) {
  return new Promise((resolve, reject) => {
    const clone = svgEl.cloneNode(true);
    // viewBox -> explicit pixel size so the bitmap is deterministic.
    const vb = (svgEl.getAttribute("viewBox") || "0 0 960 600")
      .split(/\s+/)
      .map(Number);
    const w = vb[2] || 960;
    const h = vb[3] || 600;
    clone.setAttribute("width", w);
    clone.setAttribute("height", h);
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const svgStr = new XMLSerializer().serializeToString(clone);
    const url =
      "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgStr);
    const img = new Image();
    img.onload = () => resolve({ img, w, h });
    img.onerror = reject;
    img.src = url;
  });
}

// Load a raster image (logo) with CORS enabled so the canvas stays untainted
// and toDataURL/toBlob still work. Resolves null on failure (logo is optional).
function loadImage(url) {
  return new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

// --- compose title + map + legend onto one canvas -------------------------
// legendPosition: "bottom" (wrapped rows under the map) | "right" (a vertical
// column to the right of the map, matching the editor/embed layout choice).
async function buildCanvas(svgEl, opts) {
  const {
    title = null,
    titleColor = "#1f2937",
    legendTextColor = null,
    bgColor = "#ffffff",
    transparent = false,
    legend = [], // [{ label, color }]
    legendPosition = "bottom",
    logoUrl = null,
    logoPosition = "top-right",
  } = opts;

  // Legend text auto-contrasts (black/white) against the background unless an
  // explicit color was supplied. Transparent exports assume a light host page.
  const legendInk = HEX_RE.test(legendTextColor || "")
    ? legendTextColor
    : (transparent ? "#1f2937" : pickInk(bgColor || "#ffffff"));

  const { img, w, h } = await svgToImage(svgEl);

  const pad = 24 * SCALE;
  const titleFontPx = 30 * SCALE;
  const titleH = title ? titleFontPx + 18 * SCALE : 0;
  const mapW = w * SCALE;
  const mapH = h * SCALE;

  const swatch = 16 * SCALE;
  const swatchGap = 8 * SCALE;
  const itemGap = 22 * SCALE;
  const legendFontPx = 15 * SCALE;
  const rowH = Math.max(swatch, legendFontPx) + 10 * SCALE;
  const legendGap = 28 * SCALE; // space between map and a right-side legend

  const measure = document.createElement("canvas").getContext("2d");
  measure.font = `${legendFontPx}px ${FONT}`;

  const onRight = legendPosition === "right" && legend.length > 0;

  // Right legend: one stacked row per item; column width = widest item.
  let legendColW = 0;
  let legendColH = 0;
  if (onRight) {
    for (const item of legend) {
      const tw = measure.measureText(item.label).width;
      legendColW = Math.max(legendColW, swatch + swatchGap + tw);
    }
    legendColH = legend.length * rowH;
  }

  // Bottom legend: wrapped rows that fit the map width.
  const legendRows = [];
  if (legend.length && !onRight) {
    let row = [];
    let rowW = 0;
    for (const item of legend) {
      const tw = measure.measureText(item.label).width;
      const itemW = swatch + swatchGap + tw + itemGap;
      if (rowW + itemW > mapW && row.length) {
        legendRows.push({ items: row, width: rowW });
        row = [];
        rowW = 0;
      }
      row.push({ ...item, w: itemW, textW: tw });
      rowW += itemW;
    }
    if (row.length) legendRows.push({ items: row, width: rowW });
  }
  const legendH = legendRows.length ? legendRows.length * rowH + 12 * SCALE : 0;

  const bodyW = mapW + (onRight ? legendGap + legendColW : 0);
  const bodyH = onRight ? Math.max(mapH, legendColH) : mapH + legendH;
  const totalW = bodyW + pad * 2;
  const totalH = titleH + bodyH + pad * 2;

  const canvas = document.createElement("canvas");
  canvas.width = totalW;
  canvas.height = totalH;
  const ctx = canvas.getContext("2d");

  if (!transparent) {
    ctx.fillStyle = bgColor || "#ffffff";
    ctx.fillRect(0, 0, totalW, totalH);
  }

  let y = pad;

  if (title) {
    ctx.fillStyle = titleColor || "#1f2937";
    ctx.font = `700 ${titleFontPx}px ${FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(title, totalW / 2, y);
    y += titleH;
  }

  const mapTop = y;
  ctx.drawImage(img, pad, y, mapW, mapH);

  if (onRight) {
    // Vertical column to the right of the map, top-aligned with the VT callout
    // box (matches the on-screen editor) rather than centered.
    const lx = pad + mapW + legendGap;
    let ly = y + CALLOUT_TOP_FRAC * mapH;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.font = `${legendFontPx}px ${FONT}`;
    for (const item of legend) {
      const cy = ly + rowH / 2;
      ctx.fillStyle = item.color || "#9ca3af";
      roundRect(ctx, lx, cy - swatch / 2, swatch, swatch, 3 * SCALE);
      ctx.fill();
      ctx.fillStyle = legendInk;
      ctx.fillText(item.label, lx + swatch + swatchGap, cy);
      ly += rowH;
    }
  } else {
    // Wrapped rows beneath the map, centered.
    y += mapH + 12 * SCALE;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.font = `${legendFontPx}px ${FONT}`;
    for (const r of legendRows) {
      let x = (totalW - r.width) / 2 + pad / 2;
      const cy = y + rowH / 2;
      for (const item of r.items) {
        ctx.fillStyle = item.color || "#9ca3af";
        roundRect(ctx, x, cy - swatch / 2, swatch, swatch, 3 * SCALE);
        ctx.fill();
        ctx.fillStyle = legendInk;
        ctx.fillText(item.label, x + swatch + swatchGap, cy);
        x += item.w;
      }
      y += rowH;
    }
  }

  // Logo overlay on the map, at the chosen corner (aspect preserved, never
  // stretched — scaled to fit a small box).
  const logo = await loadImage(logoUrl);
  if (logo && logo.width && logo.height) {
    const margin = mapW * 0.02;
    const boxW = mapW * 0.22;
    const boxH = mapH * 0.14;
    const scale = Math.min(boxW / logo.width, boxH / logo.height);
    const lw = logo.width * scale;
    const lh = logo.height * scale;
    const left = pad + margin;
    const right = pad + mapW - margin - lw;
    const top = mapTop + margin;
    const bottom = mapTop + mapH - margin - lh;
    const pos = {
      "top-left": [left, top],
      "top-right": [right, top],
      "bottom-left": [left, bottom],
      "bottom-right": [right, bottom],
    }[logoPosition] || [right, top];
    ctx.drawImage(logo, pos[0], pos[1], lw, lh);
  }

  return canvas;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Pad a canvas out to a target aspect ratio (w/h), centering the original and
// filling the added margin with bgColor — so the composition fills a fixed-aspect
// frame (e.g. a PPTX slide) edge-to-edge with no distortion and no crop.
function padToAspect(src, targetAspect, bgColor) {
  const srcAspect = src.width / src.height;
  let w = src.width;
  let h = src.height;
  if (srcAspect < targetAspect) {
    w = Math.round(src.height * targetAspect); // too tall/narrow -> widen
  } else if (srcAspect > targetAspect) {
    h = Math.round(src.width / targetAspect);  // too wide -> heighten
  }
  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  const ctx = out.getContext("2d");
  if (bgColor) {
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, w, h);
  }
  ctx.drawImage(src, Math.round((w - src.width) / 2), Math.round((h - src.height) / 2));
  return out;
}

function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function canvasToBlob(canvas, type = "image/png") {
  return new Promise((resolve) => canvas.toBlob(resolve, type, 1));
}

const safe = (name) =>
  (name || "distribution-map").replace(/[^a-z0-9-_]+/gi, "-").toLowerCase();

// --- public exporters -----------------------------------------------------
export async function exportPNG(svgEl, opts) {
  const canvas = await buildCanvas(svgEl, opts);
  const blob = await canvasToBlob(canvas, "image/png");
  download(blob, `${safe(opts.fileName)}.png`);
}

export async function exportPDF(svgEl, opts) {
  // Honor transparency: a transparent backdrop produces an alpha PNG that jsPDF
  // embeds with a soft mask, so the PDF carries no opaque background box.
  const canvas = await buildCanvas(svgEl, opts);
  const dataUrl = canvas.toDataURL("image/png");
  const { jsPDF } = await import("jspdf");
  const landscape = canvas.width >= canvas.height;
  const pdf = new jsPDF({
    orientation: landscape ? "landscape" : "portrait",
    unit: "pt",
    format: [canvas.width / SCALE, canvas.height / SCALE],
  });
  pdf.addImage(
    dataUrl,
    "PNG",
    0,
    0,
    canvas.width / SCALE,
    canvas.height / SCALE
  );
  pdf.save(`${safe(opts.fileName)}.pdf`);
}

export async function exportPPTX(svgEl, opts) {
  // Standard 4:3 (10 × 7.5 in) or Widescreen 16:9 (13.333 × 7.5 in).
  const wide = opts.slideFormat === "16x9";
  const slideW = wide ? 13.333 : 10;
  const slideH = 7.5;

  const content = await buildCanvas(svgEl, { ...opts, transparent: false });
  // Pad the composition out to the slide's exact aspect (filling with the map
  // background) so the image fills the WHOLE slide — edge to edge, no margin,
  // no distortion — for both 4:3 and 16:9.
  const canvas = padToAspect(content, slideW / slideH, opts.bgColor || "#ffffff");

  // Company (account) logo, branded in the top-left corner of the slide.
  const companyLogo = await loadImage(opts.companyLogoUrl);
  if (companyLogo && companyLogo.width && companyLogo.height) {
    const ctx = canvas.getContext("2d");
    const margin = canvas.width * 0.025;
    const boxW = canvas.width * 0.2;
    const boxH = canvas.height * 0.12;
    const scale = Math.min(boxW / companyLogo.width, boxH / companyLogo.height);
    ctx.drawImage(companyLogo, margin, margin, companyLogo.width * scale, companyLogo.height * scale);
  }

  const dataUrl = canvas.toDataURL("image/png");

  const PptxGenJS = (await import("pptxgenjs")).default;
  const pptx = new PptxGenJS();
  pptx.layout = wide ? "LAYOUT_16x9" : "LAYOUT_4x3";
  const slide = pptx.addSlide();
  slide.addImage({ data: dataUrl, x: 0, y: 0, w: slideW, h: slideH });
  await pptx.writeFile({ fileName: `${safe(opts.fileName)}.pptx` });
}
